import { removeRetailerTokenFromUrl } from "@faire/deprecated-retailer-visitor-shared/lib/removeRetailerTokenFromUrl";
import { useChatBubbleStore } from "@faire/deprecated-retailer-visitor-shared/packages/messenger/views/useChatBubbleStore";
import { setTimeoutIfPossible } from "@faire/next-utils/isomorphic/setTimeoutIfPossible";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import { getDocumentOrThrow } from "@faire/web/common/globals/getDocument";
import { getLocation } from "@faire/web/common/globals/getLocation";
import React, { useState, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet";

interface IProps {
  children?: React.ReactNode | undefined;
  title?: string | null;
  image?: IImage;
  prev?: number;
  next?: number;
  noindex?: boolean;
  description?: string | null;
  selfCanonical?: boolean;
}

const noIndexStashedElements = [
  "link[rel=next]:not([data-react-helmet])",
  "link[rel=prev]:not([data-react-helmet])",
];

const descriptionStashedElements = [
  "meta[name=description]:not([data-react-helmet])",
];

/**
 * Generally used for logged out pages to hence SEO + the crawlers
 * ability to index pages
 */
export const FaireHelmet: React.FunctionComponent<IProps> = ({
  title,
  image,
  prev,
  next,
  noindex,
  description,
  selfCanonical,
}) => {
  const [oldTitle, setOldTitle] = useState("");
  const [stashedElements, setStashedElements] = useState<Element[]>([]);
  const [, setChatBubbleStore] = useChatBubbleStore([]);

  useEffect(() => {
    // align open graph title and description with the title and description props
    // by default og:title and og:description should be the same as title and description
    if (title) {
      getDocumentOrThrow()
        .querySelector(`meta[property="og:title"]`)
        ?.setAttribute("content", title);
    }
    if (description) {
      getDocumentOrThrow()
        .querySelector(`meta[property="og:description"]`)
        ?.setAttribute("content", description);
    }
  }, [description, title]);

  useEffect(() => {
    setOldTitle(getDocumentOrThrow().title);
  }, []);

  useEffect(() => {
    // hack to force chrome tab to update correctly
    getDocumentOrThrow().title = `${title || oldTitle}.`;
    getDocumentOrThrow().title = title || oldTitle;
    setChatBubbleStore({ tabTitle: title ?? undefined });
    return () => {
      getDocumentOrThrow().title = oldTitle;
      setChatBubbleStore({ tabTitle: undefined });
    };
  }, [title, setChatBubbleStore, oldTitle, stashedElements]);

  const stashElements = useCallback(
    (elementsToStash: string[], afterTimeout: boolean = true) => {
      const elementsToRemove = getDocumentOrThrow().head.querySelectorAll(
        elementsToStash.join(",")
      );
      if (elementsToRemove.length === 0) {
        return;
      }
      const stash = () => {
        setStashedElements((stashedElements) =>
          stashedElements.concat(Array.from(elementsToRemove))
        );
        elementsToRemove.forEach((element) => element.remove());
      };
      if (afterTimeout) {
        setTimeoutIfPossible(stash, 0);
      } else {
        stash();
      }
    },
    []
  );

  useEffect(() => {
    if (stashedElements.length > 0) {
      return;
    }
    if (noindex) {
      stashElements(noIndexStashedElements);
    }
    if (description) {
      stashElements(descriptionStashedElements, false);
    }
    return () => {
      if (stashedElements.length > 0) {
        stashedElements.forEach((element) => {
          getDocumentOrThrow().head.appendChild(element);
        });
      }
    };
  }, [description, noindex, stashElements, stashedElements]);

  const urlWithoutParams =
    (getLocation()?.origin || "") + (getLocation()?.pathname || "");

  return (
    <Helmet>
      {title ? <title data-faire-helmet="true">{title}</title> : null}
      {image?.url ? <meta property="og:image" content={image.url} /> : null}
      {image?.width ? (
        <meta property="og:image:width" content={image.width.toString()} />
      ) : null}
      {image?.height ? (
        <meta property="og:image:height" content={image.height.toString()} />
      ) : null}
      {image?.url?.endsWith(".png") ? (
        <meta property="og:image:type" content="image/png" />
      ) : null}
      {prev ? (
        <link
          rel="prev"
          href={`${removeRetailerTokenFromUrl(urlWithoutParams)}${
            prev > 1 ? `?page=${prev}` : ""
          }`}
        />
      ) : null}
      {next ? (
        <link
          rel="next"
          href={`${removeRetailerTokenFromUrl(urlWithoutParams)}?page=${next}`}
        />
      ) : null}
      {noindex ? <meta name="robots" content="noindex" /> : null}
      {description ? <meta content={description} name="description" /> : null}
      {selfCanonical ? <link href={urlWithoutParams} rel="canonical" /> : null}
    </Helmet>
  );
};
